import {PointCloud} from './point-cloud';
import {RawPointCloudRootNode, RawPointCloudNode} from './raw-pointcloud-node';
import LocalScene from '../projections';
import { PythonExecutable } from '../../../executable';
import { Vector3 } from 'three';

export class RawPointCloud extends PointCloud {
  private workerExe: PythonExecutable;

  private file: any = null;
  private header: any = null;
  public loaded: any = false;
  private loading: boolean;
  private loadStart: any;
  private timeEnd: any;
  private byteCutoffSize = 10 * 1024**3;
  private pointsDataParsed = 0;
  private pointsDataArray = [];
  private numSampledPoints = 5 * 1000000;
  private minNumNodes = 100;
  private minPointSize;
  public generatingChunks: Boolean = false;
  public canReadData: Boolean = false;
  public percent;

  constructor(pointclouds) {
    super(pointclouds);

    this.minPointSize = Math.floor(this.numSampledPoints/this.minNumNodes);
    this.workerExe = new PythonExecutable();
  }

  get shift() {
    return LocalScene.origin;
  }

  get size() {
    return Math.max.apply(null, this.header.size);
  }

  /** Get bounding box with global shift applied */
  get tightBoundingBox() {
    let box = this._tightBoundingBox;
    if (box === null) {
      return box;
    }

    let boundingBox = {
      min: new Vector3().add(box.min).sub(this.shift),
      max: new Vector3().add(box.max).sub(this.shift)
    };

    return boundingBox;
  }

  /** Get estimated bounding box using header information */
  get estimatedBoundingBox() {
    return {
      max: new Vector3(...this.header.maxs).sub(this.shift),
      min: new Vector3(...this.header.mins).sub(this.shift)
    };
  }

  get isRemoteFile() {
    return this.file.remote;
  }

  get fromJSON() {
    return this.file.fromJSON;
  }

  get isLargeFile() {
    let isLargeFile = this.header
      ? this.header.number_of_bytes > this.byteCutoffSize
      : false;

    return isLargeFile;
  }

  /** @private */
  destroyWorker() {
    this.workerExe.destroy();
  }

  fileLoadStart() {
    this.loading = true;
    this.loaded = false;
    this.updatePercent(0);
  }

  fileLoadFinish() {
    this.loading = false;
    this.loaded = true;
    this.timeEnd = performance.now();
    this.updatePercent(100);
    const elapsed = (this.timeEnd - this.loadStart) / 1000;
    console.log(`Point cloud loaded: ${this.file.name}. ${elapsed.toFixed(2)}s`);
  }

  fileLoadCancel() {
    this.loading = false;
    this.loaded = false;
  }

  fileLoadProgress() {
    let maxDataChunks = this.header.number_of_chunks;
    let percent = this.pointsDataParsed / maxDataChunks;
    percent = Math.round(percent * 1000) / 10.0;
    percent = Math.min(100, percent);
    this.updatePercent(percent);
  }

  updatePercent(percent) {
    this.percent = percent;
  }

  updateRootNode() {
    this.root.updateSizeInfo(this.tightBoundingBox);
  }

  loadFile(file) {
    this.file = file;

    this.pointsDataParsed = 0;
    this.pointsDataArray = [];
    this.canReadData = true;

    // Add pointcloud and set parameters
    this.pointclouds.addPointCloud(this);
    this.setIdentifier(file.identifier);
    this.setVisibility(file.visible);

    this.fileLoadStart();
  }

  fileLoadFinished(success) {
    if (success) {
      this.fileLoadFinish();
    } else {
      this.fileLoadCancel();
    }
  }

  launchExecutable() {
    this.loadStart = performance.now();
    this.generatingChunks = true;

    const lasFilePath = this.file.path;
    const projectionsUnits = LocalScene.dataProjectionUnits;

    let isValid = false;
    let commands = [
      "-p", "convert_engine_las",
      "--input_file", lasFilePath,
      "--units", projectionsUnits,
      "--min_chunk_size", this.minPointSize,
      "--max_points", this.numSampledPoints
    ];

    this.workerExe.run({
      command: commands,
      parseJSON: false,
      onData: (buffer, keyword) => {
        const callback = response => {
          let {type, data} = response;
          if (type === "header") {
            this.parseHeaderResponse(data);
          } else if (type === "points") {
            this.parsePointsResponse(data);
          }
        };

        // Decode buffer data asynchronously
        let payload = {buffer: buffer.buffer, keyword};
        this.pointclouds.pushToWorkerQueue(payload, callback);

        isValid = true;
      },
      onClose: () => {
        if (isValid) {
          return;
        }

        console.error(`Error loading point cloud: ${this.file.path}`);
        this.viewer.showMessage(`Error loading point cloud: ${this.file.name}`, {
          variant: "error"
        });
        this.fileMissing = true;
        this.fileLoadFinished(true);
      }
    });
  }

  parseHeaderResponse(data) {
    this.header = data;
    this.root = new RawPointCloudRootNode(this);

    if (!this.isLargeFile) {
      return;
    }

    console.warn(`Loading large point cloud: ${this.file.path}`);
    this.viewer.showMessage(`Large point cloud file detected: ${this.file.name}`, {
      variant: "warning"
    });
  }

  parsePointsResponse(data) {
    this.pointsDataArray.push(data);
  }

  parseAvailableChunks() {
    if (!this.enabled) {
      return;
    }

    if (this.pointsDataArray.length === 0) {
      return;
    }

    const maxDataChunks = this.header?.number_of_chunks;
    const isLastChunk = (this.pointsDataParsed + 1) === maxDataChunks;
    const data = this.pointsDataArray.shift();
    this.pointsDataParsed += 1;

    // Update loaded percent
    this.fileLoadProgress();

    // Add new node from json response
    this.addNodeFromData(data);

    if (!this.enabled) {
      return;
    }

    this.pointclouds.updateMinimapExtent();

    if (!this.fromJSON) {
      let bbox = this.estimatedBoundingBox;
      this.pointclouds.zoomToExtent(bbox, false, false);
    }

    if (isLastChunk) {
      this.fileLoadFinished(true);
    }
  }

  addNodeFromData(data) {
    // Convert array to vector
    data.offset = new Vector3(...data.offset);
    data.xyz_min = new Vector3(...data.xyz_min);
    data.xyz_max = new Vector3(...data.xyz_max);

    // Convert raw buffer to float array
    data.positions = new Float32Array(data.positions);
    data.intensity = new Float32Array(data.intensity);
    data.classification = new Float32Array(data.classification);
    data.colors = new Float32Array(data.colors);
    data.gpu_index = new Int32Array(data.numPoints);

    if (this.enabled) {
      // Add new node to pointcloud
      let node = new RawPointCloudNode(this, this.root);
      node.loadFromData(data);
    }
  }
}
